import { Injectable, inject } from '@angular/core';
import { ActivatedRouteSnapshot } from '@angular/router';
import { getAllRouteParams } from '../../lib/get-all-route-params';
import { getFullPath } from '../../lib/get-full-path';
import { PICFLOW_CONFIG } from '../../picflow-config';
import { BrowserService } from '../../services/browser.service';
import { GalleryService } from '../../services/gallery.service';
import { JsonLDService } from '../../services/json-ld.service';
import { PublicTenantService } from '../../services/public-tenant.service';
import { getTenantJsonLdOrg } from '../libs/get-tenant-json-ld-org';
import { getTenantFromSnapshot } from './tenant-public.guard';

@Injectable({
  providedIn: 'root',
})
export class GalleryJsonLdGuard {
  private publicTenantService = inject(PublicTenantService);
  private browserService = inject(BrowserService);
  private galleryService = inject(GalleryService);
  private jsonLdService = inject(JsonLDService);
  private config = inject(PICFLOW_CONFIG);

  private async getGallery(next: ActivatedRouteSnapshot) {
    const params = getAllRouteParams(next);
    const galleryPathOrId = params.galleryPath || params.galleryId;
    if (!galleryPathOrId) return;
    return await this.galleryService.getPublicGalleryFromSnapshot(next);
  }

  async canActivate(next: ActivatedRouteSnapshot): Promise<true> {
    try {
      const gallery = await this.getGallery(next);
      const tenant = await getTenantFromSnapshot(
        next,
        this.publicTenantService,
        this.browserService,
        this.galleryService
      );
      if (tenant) {
        const dateCreated = gallery ? new Date(gallery.created_at * 1000).toISOString() : undefined;
        this.jsonLdService.add('page', {
          '@context': 'https://schema.org',
          '@type': 'ImageGallery',
          creator: getTenantJsonLdOrg(tenant),
          url: `${this.config.origin}/${getFullPath(next).join('/')}`,
          dateCreated,
        });
      }
    } catch {}

    return true;
  }
}
